import React from 'react';
import { repartirTotalSurLignes, formatPU, getDeviseCourante } from '../../utils/format';

// ApproA4 — rendu d'un approvisionnement au format A4 portrait (bon de
// réception). Structure proche de BonCommandeA4 mais adaptée :
//   - Titre : "BON DE RÉCEPTION" (la marchandise est entrée en stock)
//   - Référence : APP-XXXXXXXX (8 premiers caractères de l'UUID)
//   - Lien vers le bon de commande source si applicable
//   - Pas de "livraison prévue" (livraison effective = date appro)
//
// Utilisable inline pour preview et cible de window.print() / jsPDF.

function fmtGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}
function fmtDateFR(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

export default function ApproA4({ appro, fournisseur = {}, parametres = {}, logoUrl = '/logoSV.png' }) {
  if (!appro) return null;

  const nomEntreprise = parametres.nom_entreprise || 'Mon entreprise';
  const adresse       = parametres.adresse || '';
  const ville         = parametres.ville || 'Conakry, Guinée';
  const telephoneEnt  = parametres.telephone || '';
  const emailEnt      = parametres.email || '';

  const lignes = appro.lignes || [];
  const nbArticles = lignes.reduce((s, l) => s + Number(l.quantite || 0), 0);
  const reference = `APP-${String(appro.id || '').slice(0, 8).toUpperCase()}`;

  return (
    <>
      <div
        id="appro-print"
        style={{
          background: '#fff', color: '#111',
          padding: '24px 28px', maxWidth: '780px',
          margin: '0 auto',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          fontSize: '11px', lineHeight: 1.4, boxSizing: 'border-box',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: '16px',
          paddingBottom: '14px', borderBottom: '2px solid #111',
        }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <img src={logoUrl} alt="logo"
              style={{ width: '56px', height: '56px', objectFit: 'contain',
                       border: '0.5px solid #ddd', borderRadius: '4px', padding: '2px' }} />
            <div>
              <p style={{ fontWeight: 600, fontSize: '14px', margin: '0 0 2px' }}>{nomEntreprise}</p>
              {adresse && <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>{adresse}</p>}
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>{ville}</p>
              {telephoneEnt && <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Tél : {telephoneEnt}</p>}
              {emailEnt && <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Email : {emailEnt}</p>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontWeight: 600, fontSize: '18px', margin: '0 0 6px', letterSpacing: '1px' }}>
              BON DE RÉCEPTION
            </p>
            <table style={{ borderCollapse: 'collapse', marginLeft: 'auto', fontSize: '10px' }}>
              <tbody>
                <tr><td style={{ padding: '1px 8px 1px 0', color: '#555' }}>Réf.</td>
                    <td style={{ padding: '1px 0', fontWeight: 600 }}>{reference}</td></tr>
                <tr><td style={{ padding: '1px 8px 1px 0', color: '#555' }}>Date</td>
                    <td style={{ padding: '1px 0' }}>{fmtDateFR(appro.date)}</td></tr>
                {appro.bon_commande_numero && (
                  <tr><td style={{ padding: '1px 8px 1px 0', color: '#555' }}>Suite à</td>
                      <td style={{ padding: '1px 0' }}>Bon {appro.bon_commande_numero}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '12px', margin: '16px 0',
        }}>
          <div style={{ border: '0.5px solid #ccc', borderRadius: '4px', padding: '10px 12px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Fournisseur
            </p>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '12px' }}>
              {fournisseur.nom || '—'}
            </p>
            {fournisseur.specialite && (
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Spécialité : {fournisseur.specialite}</p>
            )}
            {fournisseur.adresse && (
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>{fournisseur.adresse}</p>
            )}
            {fournisseur.telephone && (
              <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Tél : {fournisseur.telephone}</p>
            )}
          </div>
          <div style={{ border: '0.5px solid #ccc', borderRadius: '4px', padding: '10px 12px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Réception enregistrée par
            </p>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '12px' }}>{appro.user_nom || '—'}</p>
            <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>Devise : {getDeviseCourante()}</p>
            <p style={{ margin: 0, color: '#555', fontSize: '10px' }}>
              Stock mis à jour le {fmtDateFR(appro.created_at)}
            </p>
          </div>
        </div>

        <table style={{
          width: '100%', borderCollapse: 'collapse',
          fontSize: '10.5px', margin: '8px 0', tableLayout: 'fixed',
        }}>
          <colgroup>
            <col style={{ width: '26px' }} />
            <col />
            <col style={{ width: '80px' }} />
            <col style={{ width: '42px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '92px' }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f5f5f5', borderTop: '1px solid #111', borderBottom: '1px solid #111' }}>
              <th style={thS('center')}>#</th>
              <th style={thS('left')}>Désignation</th>
              <th style={thS('left')}>Réf.</th>
              <th style={thS('center')}>Qté</th>
              <th style={thS('right')}>P.U. ({getDeviseCourante()})</th>
              <th style={thS('right')}>Total ({getDeviseCourante()})</th>
            </tr>
          </thead>
          <tbody>
            {/* Réconciliation affichage : les sous-totaux des lignes somment
                EXACTEMENT au total payé (corrige l'arrondi des conditionnements). */}
            {repartirTotalSurLignes(lignes.filter(l => l.variante_id), appro.cout_total).map((l, i) => {
              const qte = Number(l.quantite || 0);
              const st  = Number(l.total_affiche);
              const nom = [l.produit_nom, l.modele, l.taille, l.couleur && `(${l.couleur})`]
                .filter(Boolean).join(' ');
              return (
                <tr key={l.variante_id || i} style={{ borderBottom: '0.5px solid #eee', verticalAlign: 'top' }}>
                  <td style={tdS('center', '#888')}>{i + 1}</td>
                  <td style={{ ...tdS('left'), wordWrap: 'break-word', overflowWrap: 'break-word' }}>{nom}</td>
                  <td style={{ ...tdS('left', '#555'), fontFamily: 'monospace', fontSize: '10px' }}>{l.code_variante || '—'}</td>
                  <td style={tdS('center')}>{qte}</td>
                  <td style={tdSNum('right')}>{formatPU(l.pu_affiche)}</td>
                  <td style={{ ...tdSNum('right'), fontWeight: 600 }}>{st.toLocaleString('fr-FR')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '11px', minWidth: '240px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '4px 12px 4px 0', color: '#555' }}>Nombre d'articles reçus</td>
                <td style={{ padding: '4px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {nbArticles} pièces
                </td>
              </tr>
              <tr style={{ borderTop: '2px solid #111' }}>
                <td style={{ padding: '8px 12px 8px 0', fontWeight: 600, fontSize: '13px' }}>TOTAL PAYÉ</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600, fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtGNF(appro.cout_total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {appro.notes && (
          <div style={{ marginTop: '16px', border: '0.5px solid #ccc', borderRadius: '4px', padding: '10px 12px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Notes
            </p>
            <p style={{ margin: 0, fontSize: '10px', color: '#555' }}>{appro.notes}</p>
          </div>
        )}

        <div style={{ marginTop: '18px', textAlign: 'center', fontSize: '9px', color: '#999' }}>
          {nomEntreprise} · Document généré le {fmtDateFR(new Date())} · {reference}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body * { visibility: hidden; }
          #appro-print, #appro-print * { visibility: visible; }
          #appro-print {
            position: fixed; top: 0; left: 0;
            width: 100%; max-width: none;
            padding: 0;
          }
        }
      `}</style>
    </>
  );
}

function thS(align) {
  return {
    textAlign: align, padding: '6px 4px',
    fontSize: '9.5px', fontWeight: 600, textTransform: 'uppercase',
  };
}
function tdS(align, color = '#111') {
  return { textAlign: align, padding: '6px 4px', color };
}
function tdSNum(align) {
  return { ...tdS(align), whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
}
